import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Search, Copy, Package, Filter, CheckCircle, UserPlus, X, Loader2, CreditCard, FileText, AlertCircle, ShieldCheck } from 'lucide-react';
import { useAlert } from '../context/AlertContext';
import { useConfirm } from '../context/ConfirmContext';
import API_BASE_URL from '../config/api';

const EPinUnused = () => {
    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();
    const [searchTerm, setSearchTerm] = useState('');
    const [pins, setPins] = useState([]);
    const [loading, setLoading] = useState(true);

    // Activation Modal State
    const [showActivateModal, setShowActivateModal] = useState(false);
    const [selectedPin, setSelectedPin] = useState(null);
    const [targetUserId, setTargetUserId] = useState('');
    const [targetUserData, setTargetUserData] = useState(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [activating, setActivating] = useState(false);
    const [redeemCode, setRedeemCode] = useState('');
    const [transactionPin, setTransactionPin] = useState('');
    const [redeeming, setRedeeming] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/epins_unused.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setPins(data.data);
            }
        } catch (error) {
            console.error("Failed to fetch unused epins", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCopy = (pin) => {
        navigator.clipboard.writeText(pin);
        showAlert('Pin copied to clipboard', 'success');
    };

    // Redeem E-Pin for Self
    const handleRedeemSelf = async (code = null) => {
        const pinToRedeem = code || redeemCode;
        if (!pinToRedeem) {
            showAlert('Please enter or select an E-Pin code', 'error');
            return;
        }

        const confirmed = await showConfirm(
            'Redeem E-Pin',
            `Are you sure you want to redeem pin ${pinToRedeem} for yourself? This will add the amount to your shopping wallet.`,
            'success'
        );

        if (!confirmed) return;

        setRedeeming(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/redeem_epin.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    epinCode: pinToRedeem,
                    transactionPin: transactionPin
                })
            });
            const data = await response.json();

            if (data.success) {
                showAlert(data.message, 'success', 'E-Pin Redeemed');
                setRedeemCode('');
                fetchData(); // Refresh list
            } else {
                showAlert(data.message || 'Redemption failed', 'error');
            }
        } catch (error) {
            showAlert('Failed to redeem E-Pin', 'error');
        } finally {
            setRedeeming(false);
        }
    };

    // Open activation modal
    const handleActivateClick = (pin) => {
        setSelectedPin(pin);
        setTargetUserId('');
        setTargetUserData(null);
        setShowActivateModal(true);
    };

    // Lookup user by ID
    const handleLookupUser = async () => {
        if (!targetUserId.trim()) {
            showAlert('Please enter a User ID', 'error');
            return;
        }

        setLookupLoading(true);
        setTargetUserData(null);

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/lookup_user.php?user_id=${encodeURIComponent(targetUserId.trim())}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success) {
                setTargetUserData(data.data);
            } else {
                showAlert(data.message || 'User not found', 'error');
            }
        } catch (error) {
            showAlert('Failed to lookup user', 'error');
        } finally {
            setLookupLoading(false);
        }
    };

    // Activate user with E-Pin
    const handleActivateUser = async () => {
        if (!targetUserData || !selectedPin) {
            showAlert('Please lookup a valid user first', 'error');
            return;
        }

        const confirmed = await showConfirm(
            'Confirm Activation',
            `Are you sure you want to activate ${targetUserData.name} (${targetUserData.user_code}) with the ${selectedPin.package} package worth ₹${selectedPin.amount.toLocaleString()}?`,
            'success'
        );

        if (!confirmed) return;

        setActivating(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/activate_user_epin.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    epin_id: selectedPin.id,
                    target_user_id: targetUserData.user_code,
                    transaction_pin: transactionPin
                })
            });
            const data = await response.json();

            if (data.success) {
                showAlert(data.message, 'success', 'Activation Successful');
                setShowActivateModal(false);
                fetchData(); // Refresh pins list
            } else {
                showAlert(data.message || 'Activation failed', 'error');
            }
        } catch (error) {
            showAlert('Failed to activate user', 'error');
        } finally {
            setActivating(false);
        }
    };

    const filteredPins = pins.filter(p =>
        p.pin.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.package.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Unused E-Pins</h1>
                        <p className="text-gray-500 text-sm mt-1">Manage your active inventory or redeem pins for yourself</p>
                    </div>
                </div>

                {/* Quick Redeem Bar */}
                <div className="bg-gradient-to-r from-indigo-600 to-blue-700 rounded-2xl p-6 shadow-xl shadow-indigo-100 text-white">
                    <div className="flex flex-col md:flex-row items-center gap-6">
                        <div className="flex-shrink-0 p-3 bg-white/20 rounded-xl">
                            <CreditCard size={32} />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-bold">Redeem E-Pin</h2>
                            <p className="text-indigo-100 text-sm">Enter a code to instantly credit your shopping wallet</p>
                        </div>
                        <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                            <input
                                type="text"
                                placeholder="E-Pin Code"
                                value={redeemCode}
                                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                                className="w-full md:w-48 px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl focus:bg-white/20 focus:outline-none placeholder:text-indigo-200 font-mono text-center uppercase"
                            />
                            <input
                                type="password"
                                maxLength={4}
                                placeholder="PIN"
                                value={transactionPin}
                                onChange={(e) => setTransactionPin(e.target.value.replace(/\D/g, ''))}
                                className="w-full md:w-24 px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl focus:bg-white/20 focus:outline-none placeholder:text-indigo-200 text-center"
                            />
                            <button
                                onClick={() => handleRedeemSelf()}
                                disabled={redeeming || !transactionPin}
                                className="px-6 py-2.5 bg-white text-indigo-600 rounded-xl font-bold hover:bg-indigo-50 transition-colors shadow-lg disabled:opacity-50"
                            >
                                {redeeming ? 'Processing...' : 'Redeem'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                        <div className="p-3 bg-green-50 text-green-600 rounded-xl">
                            <CheckCircle size={24} />
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs font-bold uppercase">Total Available</p>
                            <h3 className="text-2xl font-bold text-gray-900">{pins.length}</h3>
                        </div>
                    </div>
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                        <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                            <Package size={24} />
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs font-bold uppercase">Starter Pack</p>
                            <h3 className="text-2xl font-bold text-gray-900">{pins.filter(p => p.package === 'Starter').length}</h3>
                        </div>
                    </div>
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                        <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
                            <UserPlus size={24} />
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs font-bold uppercase">Ready to Activate</p>
                            <h3 className="text-2xl font-bold text-gray-900">{pins.length}</h3>
                        </div>
                    </div>
                </div>

                {/* Tools */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between gap-4">
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search by PIN or Package..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2">
                        <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-600 font-medium transition-colors">
                            <Filter size={18} /> Filter
                        </button>
                    </div>
                </div>

                {/* Grid of Pins */}
                {loading ? (
                    <div className="text-center py-12 text-gray-400">
                        <Loader2 className="animate-spin mx-auto mb-2" size={32} />
                        Loading pins...
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredPins.map(pin => (
                            <div key={pin.id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
                                <div className="flex justify-between items-start mb-4">
                                    <div className={`px-3 py-1 rounded-lg text-xs font-bold uppercase ${pin.package === 'Starter' ? 'bg-blue-50 text-blue-600' :
                                        pin.package === 'Pro' ? 'bg-purple-50 text-purple-600' : 'bg-amber-50 text-amber-600'
                                        }`}>
                                        {pin.package}
                                    </div>
                                    <span className="text-xs text-gray-400 font-mono">{pin.date}</span>
                                </div>

                                <div className="bg-gray-50 rounded-xl p-3 mb-4 border border-gray-200 flex items-center justify-between">
                                    <code className="font-mono font-bold text-gray-800 tracking-wide text-lg">{pin.pin}</code>
                                    <button
                                        onClick={() => handleCopy(pin.pin)}
                                        className="p-1.5 hover:bg-white rounded-lg text-gray-400 hover:text-indigo-600 transition-colors"
                                        title="Copy PIN"
                                    >
                                        <Copy size={16} />
                                    </button>
                                </div>

                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-sm font-semibold text-gray-900">₹ {pin.amount.toLocaleString()}</div>
                                    <div className="flex gap-1.5">
                                        <button
                                            onClick={() => handleRedeemSelf(pin.pin)}
                                            className="px-3 py-1.5 border border-indigo-200 text-indigo-600 text-xs font-bold rounded-lg hover:bg-indigo-50 transition-colors"
                                        >
                                            Redeem Self
                                        </button>
                                        <button
                                            onClick={() => handleActivateClick(pin)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                                        >
                                            <UserPlus size={14} /> Activate User
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && filteredPins.length === 0 && (
                    <div className="text-center py-12 text-gray-400">
                        No unused pins found matching your search.
                    </div>
                )}
            </div>

            {/* Activation Modal */}
            {showActivateModal && selectedPin && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fadeIn">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
                        onClick={() => !activating && setShowActivateModal(false)}
                    />

                    {/* Modal */}
                    <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all animate-scaleIn">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white/20 rounded-xl">
                                        <UserPlus size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold">Activate User</h3>
                                        <p className="text-sm text-green-100">Using E-Pin: {selectedPin.pin}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => !activating && setShowActivateModal(false)}
                                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Package Info */}
                        <div className="p-4 bg-gray-50 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`px-3 py-1 rounded-lg text-xs font-bold uppercase ${selectedPin.package === 'Starter' ? 'bg-blue-100 text-blue-700' :
                                        selectedPin.package === 'Pro' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'
                                        }`}>
                                        {selectedPin.package}
                                    </div>
                                    <span className="text-gray-600 font-medium">Package</span>
                                </div>
                                <div className="text-xl font-bold text-gray-900">₹ {selectedPin.amount.toLocaleString()}</div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-4">
                            {/* User ID Input */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Enter User ID</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={targetUserId}
                                        onChange={(e) => setTargetUserId(e.target.value.toUpperCase())}
                                        placeholder="e.g. NX123456"
                                        className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none font-mono uppercase"
                                        disabled={activating}
                                    />
                                    <button
                                        onClick={handleLookupUser}
                                        disabled={lookupLoading || activating}
                                        className="px-4 py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {lookupLoading ? <Loader2 size={20} className="animate-spin" /> : 'Lookup'}
                                    </button>
                                </div>
                            </div>

                            {/* User Details Card */}
                            {targetUserData && (
                                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-lg">
                                            {targetUserData.name.charAt(0)}
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-bold text-gray-900">{targetUserData.name}</h4>
                                            <p className="text-sm text-gray-500 font-mono">{targetUserData.user_code}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-gray-400">Status</p>
                                            <p className={`text-xs font-bold ${targetUserData.is_kyc_approved ? 'text-green-600' : 'text-amber-600'}`}>
                                                {targetUserData.is_kyc_approved ? 'KYC Approved' : 'KYC Pending'}
                                            </p>
                                        </div>
                                    </div>

                                    {!targetUserData.is_kyc_approved && (
                                        <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                                            <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
                                            <div className="text-xs text-red-600">
                                                <strong>KYC Approval Required:</strong> This user cannot receive E-Pins or be activated until their KYC is approved by the administrator.
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-3 mt-4">
                                        <div className="flex items-center gap-2">
                                            <CreditCard size={16} className="text-gray-400" />
                                            <div>
                                                <p className="text-xs text-gray-500">PAN Number</p>
                                                <p className="font-semibold text-gray-800 text-sm">{targetUserData.pan_number}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <FileText size={16} className="text-gray-400" />
                                            <div>
                                                <p className="text-xs text-gray-500">Aadhaar</p>
                                                <p className="font-semibold text-gray-800 text-sm">{targetUserData.aadhaar_number}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Transaction PIN */}
                            {targetUserData && (
                                <div className="p-4 bg-gray-900 rounded-2xl text-white">
                                    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-3 ml-1">Authorize Transaction</label>
                                    <div className="relative">
                                        <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                                        <input
                                            type="password"
                                            maxLength={4}
                                            value={transactionPin}
                                            onChange={(e) => setTransactionPin(e.target.value.replace(/\D/g, ''))}
                                            placeholder="Enter 4-Digit Transaction PIN"
                                            className="w-full bg-white/10 border border-white/20 rounded-xl py-3 px-4 pl-12 font-bold focus:outline-none focus:border-green-500 focus:bg-white/20 transition-all text-sm placeholder:text-gray-600"
                                        />
                                    </div>
                                    <p className="text-[9px] text-gray-400 mt-2 ml-1">* Required to activate this user account</p>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="p-6 pt-0 flex gap-3">
                            <button
                                onClick={() => !activating && setShowActivateModal(false)}
                                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors"
                                disabled={activating}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleActivateUser}
                                disabled={!targetUserData || !targetUserData.is_kyc_approved || activating || !transactionPin}
                                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {activating ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        Activating...
                                    </>
                                ) : (
                                    <>
                                        <UserPlus size={18} />
                                        Activate User
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes scaleIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out forwards;
                }
                .animate-scaleIn {
                    animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>
        </Layout>
    );
};

export default EPinUnused;
